// Shell del juego (T-001-05, replanteado en T-001-06).
//
// La bienvenida da una acción inicial breve antes de explorar; después, la
// persona vuelve al mundo libre o entra en el episodio elegido.
//
// Hay más de un episodio. Cuál se juega lo elige quien acompaña desde la
// barra de abajo, con el título que cada episodio trae en su propio
// contenido. No hay progresión automática de uno a otro: el episodio 1
// declara `ep02.unlocked` como flag persistente, pero SPEC-001 AC-5 solo
// permite guardar `ep01.completed`, así que encadenarlos de verdad exige
// primero una decisión de spec sobre qué puede sobrevivir a la sesión. El distintivo de borrador se dibuja aquí,
// fuera del conmutador, para que esté en los dos (AC-10, Constitución IV).
//
// El modo de edad decide qué nodos y opciones existen (AC-3). El MVP presenta
// por ahora la ruta 6–8; no expone una franja de edad en la interfaz infantil.
// Un selector para quien acompaña requerirá una pantalla adulta explícita.

import { useCallback, useState } from "react";

import type { AgeMode } from "../engine";
import { EPISODIOS } from "../shared/episodios";
import { AvisoDeDesarrollo } from "./dialogue/AvisoDeDesarrollo";
import { EpisodioEnCurso } from "./dialogue/EpisodioEnCurso";
import { EscenaLibre } from "./scene/EscenaLibre";
import { DistintivoBorrador } from "./ui/DistintivoBorrador";
import { TEXTOS_UI } from "./ui/textos-ui";
import "./ui/experiencia-movil.css";

const MODO_DE_EDAD_MVP: AgeMode = "6-8";

export default function GameShell() {
  const ageMode = MODO_DE_EDAD_MVP;
  const [inicioCompletado, setInicioCompletado] = useState(false);
  // Estado efímero de mundo: la reparación solo vive en esta sesión de React.
  // No se guarda en el dispositivo ni contiene elecciones del episodio.
  const [puentePrincipalReparado, setPuentePrincipalReparado] = useState(false);
  /**
   * `null` significa que no hay episodio y se está explorando. Se entra por
   * ahí: el mundo es el sitio del que se sale hacia los episodios y al que se
   * vuelve, no una pantalla de menú.
   */
  const [episodioId, setEpisodioId] = useState<string | null>(null);

  const volverAlMundo = useCallback((puenteReparado = false) => {
    if (puenteReparado) setPuentePrincipalReparado(true);
    setEpisodioId(null);
  }, []);

  if (episodioId === null) {
    return (
      <Mundo
        alEmpezarEpisodio={setEpisodioId}
        ageMode={ageMode}
        puentePrincipalReparado={puentePrincipalReparado}
        mostrarInicio={!inicioCompletado}
        alIniciarAventura={() => setInicioCompletado(true)}
      />
    );
  }

  const elegido = EPISODIOS.find((candidato) => candidato.id === episodioId) ?? EPISODIOS[0];

  // AC-1: si el contenido no valida, un error de desarrollo legible en vez de
  // una pantalla en blanco. La app no arranca el episodio en ese caso.
  if (!elegido || !elegido.resultado.ok) {
    return (
      <main className="shell">
        <DistintivoBorrador />
        <AvisoDeDesarrollo
          titulo={TEXTOS_UI.desarrollo.errorDeContenido}
          diagnosticos={(elegido?.resultado.ok === false ? elegido.resultado.issues : []).map(
            (issue) => ({
              code: "navegacion-rota" as const,
              path: issue.path,
              message: issue.message,
            }),
          )}
        />
      </main>
    );
  }

  const episodio = elegido.resultado.episode;
  const titulo = episodio.localization[episodio.defaultLocale]?.[episodio.titleLocId] ?? episodio.slug;

  return (
    <main className="shell shell--juego">
      {/* Encabezado flotante. Lo único que se ve es el distintivo, que no
          puede apagarse (AC-10). El título del episodio se queda como `h1`
          —hace falta para la estructura de encabezados y para quien navega
          con lector de pantalla— pero no se dibuja: en pantalla era un rótulo
          permanente que no cambia nunca, ocupando sitio del mundo. */}
      <header className="shell__encabezado shell__encabezado--flotante">
        <DistintivoBorrador />
        <h1 className="shell__titulo shell__titulo--juego visualmente-oculto">{titulo}</h1>
      </header>

      <EpisodioEnCurso
        episodio={episodio}
        ageMode={ageMode}
        alSalirAlMundo={volverAlMundo}
      />
    </main>
  );
}
/**
 * El mundo abierto, con el mismo distintivo de borrador que todo lo demás: no
 * se apaga por estar fuera de un episodio (AC-10).
 */
function Mundo({
  alEmpezarEpisodio,
  ageMode,
  puentePrincipalReparado,
  mostrarInicio,
  alIniciarAventura,
}: {
  readonly alEmpezarEpisodio: (id: string) => void;
  readonly ageMode: AgeMode;
  readonly puentePrincipalReparado: boolean;
  readonly mostrarInicio: boolean;
  readonly alIniciarAventura: () => void;
}) {
  return (
    <main className="shell shell--juego">
      {/* El `h1` no se dibuja —en pantalla sería un rótulo permanente que no
          cambia nunca, ocupando sitio del mundo— pero existe: hace falta para
          la estructura de encabezados y para quien navega con lector de
          pantalla. */}
      <header className="shell__encabezado shell__encabezado--flotante">
        <DistintivoBorrador />
        <h1 className="shell__titulo shell__titulo--juego visualmente-oculto">
          {TEXTOS_UI.mundo.region}
        </h1>
      </header>
      <EscenaLibre
        alEmpezarEpisodio={alEmpezarEpisodio}
        ageMode={ageMode}
        puentePrincipalReparado={puentePrincipalReparado}
        mostrarInicio={mostrarInicio}
        alIniciarAventura={alIniciarAventura}
      />
    </main>
  );
}
